<template>
    <div class="card">
        <div class="mt-2 mx-2">
            <h3>API Integration Hub</h3>
            <ul class="nav nav-tabs mt-3" role="tablist">
                <li class="nav-item">
                    <button class="nav-link" :class="{ active: activeTab === 'my_integrations' }" @click="activeTab = 'my_integrations'">API Provider</button>
                </li>
                <li class="nav-item">
                    <button class="nav-link" :class="{ active: activeTab === 'connected_providers' }" @click="activeTab = 'connected_providers'">API Receiver</button>
                </li>
            </ul>
        </div>
        
        <div v-if="activeTab === 'my_integrations'">
            <div class="d-flex flex-wrap justify-content-end align-item-center mt-2 mx-2">
                <div class="gx-1">
                    <button
                        style="min-width: 64px;"
                        @click="openAddProviderModal"
                        class="btn btn-primary waves-effect waves-float waves-light me-1"
                    >Add New
                    </button>
                </div>
            </div>
        
            <div class="col-12 px-2 mt-2 mb-4">
                <div class="card p-2" style="min-height: 200px">
                    <div class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Contact Profile</th>
                                    <th>Endpoint</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Expires At</th>
                                    <th class="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in providerItems" :key="i">
                                    <td>{{ item.contact_profile_name }}</td>
                                    <td>{{ item.endpoint }}</td>
                                    <td>
                                        <span v-for="(service, idx) in (item.services ? item.services.split(',') : [])" :key="idx" class="badge bg-primary me-1">
                                            {{ service }}
                                        </span>
                                    </td>
                                    <td>
                                        <span :class="item.status === 'active' ? 'badge bg-success' : 'badge bg-danger'">
                                            {{ item.status }}
                                        </span>
                                    </td>
                                    <td>{{ formatDate(item.expires_at) }}</td>
                                    <td class="text-center">
                                        <button class="btn btn-primary btn-sm me-1"
                                                @click="openEditProviderModal(item)"
                                                title="View / Edit">
                                            <i class="fas fa-eye"></i>
                                        </button>
                                        <button class="btn btn-warning btn-sm me-1"
                                                @click="regenerateProviderKey(item.id)"
                                                title="Regenerate API Key">
                                            <i class="fas fa-sync-alt"></i>
                                        </button>
                                        <button class="btn btn-danger btn-sm"
                                                @click="deleteProviderItem(item.id)"
                                                title="Delete">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-if="!loading && providerItems.length === 0" class="text-center mt-5">No integrations found</p>
                    </div>
                </div>
            </div>

            <div class="px-2 position-absolute bottom-0 mt-4">
                <Pagination :offset="providerParams.offset" @onPageChange="onProviderPageChange" ref="providerPagination"/>
            </div>
        </div>

        <div v-if="activeTab === 'connected_providers'">
            <div class="d-flex flex-wrap justify-content-end align-item-center mt-2 mx-2">
                <div class="gx-1">
                    <button
                        style="min-width: 64px;"
                        @click="openAddReceiverModal"
                        class="btn btn-primary waves-effect waves-float waves-light me-1"
                    >Add New
                    </button>
                </div>
            </div>

            <div class="col-12 px-2 mt-2 mb-4">
                <div class="card p-2" style="min-height: 200px">
                    <div class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Contact Profile</th>
                                    <th>Endpoint (URL)</th>
                                    <th>Status</th>
                                    <th class="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in receiverItems" :key="i">
                                    <td>{{ item.contact_profile_name || '' }}</td>
                                    <td>{{ item.endpoint }}</td>
                                    <td>
                                        <span :class="item.status === 'active' ? 'badge bg-success' : 'badge bg-danger'">
                                            {{ item.status }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <button class="btn btn-info btn-sm me-1"
                                                @click="pingReceiver(item.id)"
                                                title="Test Connection (Ping)">
                                            <i class="fas fa-network-wired"></i>
                                        </button>
                                        <button class="btn btn-primary btn-sm me-1"
                                                @click="openEditReceiverModal(item)"
                                                title="View / Edit">
                                            <i class="fas fa-eye"></i>
                                        </button>
                                        <button class="btn btn-danger btn-sm"
                                                @click="deleteReceiverItem(item.id)"
                                                title="Delete">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-if="!loading && receiverItems.length === 0" class="text-center mt-5">No connected providers found</p>
                    </div>
                </div>
            </div>

            <div class="px-2 position-absolute bottom-0 mt-4">
                <Pagination :offset="receiverParams.offset" @onPageChange="onReceiverPageChange" ref="receiverPagination"/>
            </div>
        </div>

        <Loader v-if="loading"/>

        <AddApiProviderModal
            v-if="store.state.isModalOpenTwo && activeTab === 'my_integrations'"
            :isEdit="isEdit"
            :formData="providerFormData"
            :editData="providerEditData"
            :servicesList="servicesList"
            :accountHeads="accountHeads"
            @onSaved="handleProviderSaved"
        />

        <AddApiReceiverModal
            v-if="store.state.isModalOpenTwo && activeTab === 'connected_providers'"
            :isEdit="isEdit"
            :formData="receiverFormData"
            :editData="receiverEditData"
            :servicesList="servicesList"
            @onSaved="handleReceiverSaved"
        />

        <ApiKeyModal 
            :isOpen="isApiKeyModalOpen" 
            :apiKey="generatedApiKey"
            @close="isApiKeyModalOpen = false"
        />
    </div>
</template>

<script setup>
import AddApiProviderModal from './AddApiProviderModal.vue'
import AddApiReceiverModal from './AddApiReceiverModal.vue'
import ApiKeyModal from './ApiKeyModal.vue'
import { inject, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleApiIntegrationHub from '@/services/modules/apiIntegrationHub'
import handleDropdowns from '@/services/modules/dropdowns'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const isEdit = ref(false)
const isApiKeyModalOpen = ref(false)
const generatedApiKey = ref('')
const activeTab = ref('my_integrations')

const providerItems = ref([]);
const providerParams = ref({ offset: 20 });
const providerEditData = ref({})
const providerFormData = ref({
    endpoint: null,
    services: [],
    status: 'active',
    expires_at: null,
})

const receiverItems = ref([]);
const receiverParams = ref({ offset: 20 });
const receiverEditData = ref({})
const receiverFormData = ref({
    endpoint: '',
    api_key: '',
    contact_profile_id: '',
    services: [],
    status: 'active'
})

const { 
    fetchApiProviders, deleteApiProvider, regenerateKey: regenKeyApi, 
    fetchApiReceivers, deleteApiReceiver, fetchThirdPartyServices,
    pingApiReceiver
} = handleApiIntegrationHub();

const { fetchDropdownList } = handleDropdowns();

const servicesList = ref([]);
const accountHeads = ref([]);

const fetchServicesList = async () => {
    try {
        const query = `?company_id=${route.params.companyId}`;
        const res = await fetchThirdPartyServices(query);
        if (res.status) {
            servicesList.value = res.data.data;
        }
    } catch (err) {
        console.error(err);
    }
}

const getDropDownQuery = () => {
    return `?company_id=${route.params.companyId}&is_account_heads=1&slags[]=cash_and_banks`;
};

const fetchAccountHeads = async () => {
    try {
        const res = await fetchDropdownList(getDropDownQuery());
        if (res.status && res.data.account_head_list_by_slag && res.data.account_head_list_by_slag.cash_and_banks) {
            accountHeads.value = res.data.account_head_list_by_slag.cash_and_banks;
        }
    } catch (err) {
        console.error(err);
    }
}

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
}

const openAddProviderModal = () => {
    isEdit.value = false
    providerFormData.value = {
        endpoint: null,
        services: [],
        status: 'active',
        expires_at: null,
        contact_profile_id: null,
    };
    providerEditData.value = {};
    store.state.isModalOpenTwo = true
}

const openEditProviderModal = (item) => {
    isEdit.value = true
    providerFormData.value = {
        id: item.id,
        endpoint: item.endpoint,
        status: item.status,
        services: item.services ? item.services.split(',') : [],
        expires_at: item.expires_at ? item.expires_at.split('T')[0] : null,
        contact_profile_id: item.contact_profile_id ? {id: item.contact_profile_id, name: item.contact_profile_name} : null,
        account_head_id: item.account_head_id || null,
    };
    providerEditData.value = item;
    store.state.isModalOpenTwo = true;
}

const handleProviderSaved = (apiKey = null) => {
    fetchProvidersData();
    if (apiKey) {
        generatedApiKey.value = apiKey;
        isApiKeyModalOpen.value = true;
    }
}

const deleteProviderItem = async (id) => {
    if (!confirm('Are you sure you want to delete this integration?')) return;
    try {
        loading.value = true;
        const query = `?company_id=${route.params.companyId}`;
        const res = await deleteApiProvider(id, query);
        if (!res.status) {
            showError(res.message || 'Failed to delete');
            return;
        }

        showSuccess(res.message || 'Deleted successfully');
        fetchProvidersData();
    } catch (err) {
        showError('An error occurred');
    } finally {
        loading.value = false;
    }
}

const regenerateProviderKey = async (id) => {
    if (!confirm('Are you sure you want to regenerate the API key and inbound secret? The old credentials will immediately stop working.')) return;
    try {
        loading.value = true;
        const query = `?company_id=${route.params.companyId}`;
        const res = await regenKeyApi(id, query);
        if (!res.status) {
            showError(res.message || 'Failed to regenerate key');
            return;
        }
        
        showSuccess('API Key regenerated successfully.');
        generatedApiKey.value = res.api_key;
        isApiKeyModalOpen.value = true;
        fetchProvidersData();
    } catch (err) {
        showError('An error occurred');
    } finally {
        loading.value = false;
    }
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const onProviderPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await fetchProvidersData();
}

const onReceiverPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await fetchReceiversData();
}

const getQuery = (typeParams) => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    query += '&per_page=' + typeParams.value.offset
    return query;
}

const fetchProvidersData = async () => {
    const query = getQuery(providerParams);
    loading.value = true
    providerItems.value = []

    try {
        const res = await fetchApiProviders(query);
        if(!res.status) {
            store.commit("resetPagination")
            return;
        }
        
        providerItems.value = res.data.data;
        setPagination(res.data);
    } catch (err) {
        console.error(err)
    } finally {
        loading.value = false
    }
}

const fetchReceiversData = async () => {
    const query = getQuery(receiverParams);
    loading.value = true
    receiverItems.value = []

    try {
        const res = await fetchApiReceivers(query);
        if(!res.status) {
            store.commit("resetPagination")
            return;
        }
        
        receiverItems.value = res.data.data;
        setPagination(res.data);
    } catch (err) {
        console.error(err)
    } finally {
        loading.value = false
    }
}

const openAddReceiverModal = () => {
    isEdit.value = false
    receiverFormData.value = {
        endpoint: '',
        api_key: '',
        contact_profile_id: '',
        services: [],
        status: 'active'
    };
    receiverEditData.value = {};
    store.state.isModalOpenTwo = true
}

const openEditReceiverModal = (item) => {
    isEdit.value = true
    receiverFormData.value = {
        id: item.id,
        endpoint: item.endpoint,
        api_key: item.api_key,
        services: item.services ? item.services.split(',') : [],
        contact_profile_id: item.contact_profile_id ? {id: item.contact_profile_id, name: item.contact_profile_name} : null,
        status: item.status
    };
    receiverEditData.value = item;
    store.state.isModalOpenTwo = true;
}

const handleReceiverSaved = () => {
    fetchReceiversData();
}

const deleteReceiverItem = async (id) => {
    if (!confirm('Are you sure you want to delete this connected provider?')) return;
    try {
        loading.value = true;
        const query = `?company_id=${route.params.companyId}`;
        const res = await deleteApiReceiver(id, query);
        if (!res.status) {
            showError(res.message || 'Failed to delete');
            return;
        }

        showSuccess(res.message || 'Deleted successfully');
        fetchReceiversData();
    } catch (err) {
        showError('An error occurred');
    } finally {
        loading.value = false;
    }
}

const pingReceiver = async (id) => {
    try {
        loading.value = true;
        const query = `?company_id=${route.params.companyId}`;
        const res = await pingApiReceiver(id, query);
        if (!res.status) {
            showError(res.message || 'Connection failed');
            return;
        }
        showSuccess(res.message || 'Connection successful!');
    } catch (err) {
        showError('An error occurred during ping');
    } finally {
        loading.value = false;
    }
}

watch(activeTab, async (newVal) => {
    const routeQuery = Object.assign({}, route.query, { page: 1 })
    await router.push({path: route.path, query: routeQuery})
    if (newVal === 'my_integrations') return fetchProvidersData();
    fetchReceiversData();
})

onMounted(() => {
    fetchProvidersData();
    fetchServicesList();
    fetchAccountHeads();
})
</script>
